'use client';

import { createStore } from 'zustand';

import { Appointment } from '@/types/appointments.types';
import { AppointmentsLifecycleEnum } from '@/types/enums/appointments.enum';

export const APPOINTMENTS_LIST_CACHE_TTL_MS = 5 * 60 * 1000;

export type AppointmentsListItem = Partial<Appointment> & { failed?: boolean };

export interface AppointmentsListSnapshot {
  items: AppointmentsListItem[];
  page: number;
  listSize: number;
}

interface AppointmentsListCacheEntry extends AppointmentsListSnapshot {
  ownerId: string;
  savedAt: number;
}

type AppointmentsListStoreState = {
  lists: Partial<Record<AppointmentsLifecycleEnum, AppointmentsListCacheEntry>>;
};

export const appointmentsListStore = createStore<AppointmentsListStoreState>(() => ({ lists: {} }));

export function readAppointmentsList(
  lifecycle: AppointmentsLifecycleEnum,
  ownerId: string | undefined,
): AppointmentsListSnapshot | undefined {
  const entry = appointmentsListStore.getState().lists[lifecycle];

  if (!entry || !ownerId || entry.ownerId !== ownerId) {
    return undefined;
  }

  if (Date.now() - entry.savedAt > APPOINTMENTS_LIST_CACHE_TTL_MS) {
    return undefined;
  }

  return { items: entry.items, page: entry.page, listSize: entry.listSize };
}

export function saveAppointmentsList(
  lifecycle: AppointmentsLifecycleEnum,
  ownerId: string | undefined,
  snapshot: AppointmentsListSnapshot,
) {
  if (!ownerId) {
    return;
  }

  appointmentsListStore.setState((state) => ({
    lists: { ...state.lists, [lifecycle]: { ...snapshot, ownerId, savedAt: Date.now() } },
  }));
}

export function updateListedAppointment(id: string, update: Partial<Appointment>) {
  appointmentsListStore.setState((state) => {
    const lists = Object.fromEntries(
      Object.entries(state.lists).map(([lifecycle, entry]) => [
        lifecycle,
        {
          ...entry,
          items: entry.items.map((item) => (item.id === id ? { ...item, ...update } : item)),
        },
      ]),
    );

    return { lists };
  });
}

export function resetAppointmentsLists() {
  appointmentsListStore.setState({ lists: {} });
}
